import React, { useState, useEffect } from 'react';
import { CheckCircle, Target, Users, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT = {
  hero_title: 'About PUSH Training - NC',
  hero_subtitle: 'Prepare Until Success Happens - Our mission is to empower individuals with the knowledge and skills to save lives in emergency situations.',
  story_title: 'Our Story',
  story_p1: 'PUSH Training - NC was founded with a simple yet powerful vision: to make life-saving education accessible to everyone in our community.',
  story_p2: 'Based in Henderson and Durham, North Carolina, we serve individuals from all walks of life - healthcare professionals, educators, business owners, parents, and anyone who wants to be prepared to help in an emergency.',
  story_p3: 'Our certified instructors bring real-world experience and a passion for teaching that creates an engaging, supportive learning environment where students gain both knowledge and confidence.',
  mission_text: 'To provide high-quality, accessible CPR and first aid training that empowers individuals to respond confidently and effectively in emergency situations, ultimately saving lives in our community.',
  community_text: 'We believe that every person has the potential to be a lifesaver. Our community-focused approach ensures training is accessible, affordable, and tailored to meet the unique needs of our students.',
  credentials_text: 'PUSH Training - NC is an authorized training center aligned with American Heart Association standards. Our instructors maintain current certifications and undergo regular quality assurance reviews to ensure you receive the highest quality training.'
};

export const About = () => {
  const [c, setC] = useState(DEFAULT);

  useEffect(() => {
    axios.get(`${API}/content/about`).then(r => setC({ ...DEFAULT, ...r.data })).catch(console.error);
  }, []);

  const APART = [
    { title: 'Expert Instruction', desc: 'Our instructors are certified by the American Heart Association and bring years of real-world experience.' },
    { title: 'Hands-On Learning', desc: "We emphasize practical, scenario-based training so you're truly prepared for emergencies." },
    { title: 'Flexible Options', desc: 'From scheduled classes to on-site group training, we work around your schedule.' },
    { title: 'Current Curriculum', desc: 'All courses follow the latest AHA guidelines and evidence-based practices.' },
    { title: 'Small Class Sizes', desc: 'We maintain low student-to-instructor ratios for personalized attention.' },
    { title: 'Immediate Certification', desc: 'Receive your digital certification card on the same day upon successful completion.' },
  ];

  return (
    <div className="min-h-screen">
      <section className="bg-gradient-to-r from-red-50 to-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">{c.hero_title}</h1>
          <p className="text-xl text-gray-600 max-w-3xl">{c.hero_subtitle}</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{c.story_title}</h2>
              <div className="space-y-4 text-gray-600 text-lg">
                <p>{c.story_p1}</p>
                <p>{c.story_p2}</p>
                <p>{c.story_p3}</p>
              </div>
            </div>
            <div className="bg-red-100 rounded-2xl p-8 lg:p-12">
              <img src="https://customer-assets.emergentagent.com/job_push-life-rescue/artifacts/5u8zfaw3_8FDE4B3D-0199-4FB7-B27F-1E811EC96128.png" alt="PUSH Training Logo" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Our Mission & Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-2 border-red-100">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4"><Target className="text-red-600" size={28} /></div>
                <CardTitle className="text-2xl">Our Mission</CardTitle>
              </CardHeader>
              <CardContent><p className="text-gray-600 text-lg">{c.mission_text}</p></CardContent>
            </Card>
            <Card className="border-2 border-red-100">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4"><Users className="text-red-600" size={28} /></div>
                <CardTitle className="text-2xl">Community First</CardTitle>
              </CardHeader>
              <CardContent><p className="text-gray-600 text-lg">{c.community_text}</p></CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">What Sets Us Apart</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {APART.map(({ title, desc }) => (
              <div key={title} className="flex gap-4">
                <CheckCircle className="text-red-600 flex-shrink-0" size={24} />
                <div><h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3><p className="text-gray-600">{desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="text-red-600 mx-auto mb-6" size={64} />
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Certified & Trusted</h2>
          <p className="text-lg text-gray-600 mb-8">{c.credentials_text}</p>
        </div>
      </section>
    </div>
  );
};
